// useTimelineLogic.js - Timeline-specific logic
import { useMemo } from 'react';
import { getISOWeek, getISOWeekYear } from 'date-fns';
import { formatMonth } from '../utils/ganttUtils';
import { addDaysNormalized } from '../../../i18n/dateUtils';
import { BUFFER_DAYS } from '../constants/ganttConstants';

export const useTimelineLogic = (scrollState, baseDate, dayWidth, visibleDays = 40) => {
  // Get visible day range
  const getVisibleDayRange = () => {
    const centerDay = Math.floor(scrollState.scrollLeft / dayWidth) + scrollState.timelineStart;
    const startDay = Math.max(scrollState.timelineStart, centerDay - BUFFER_DAYS);
    const endDay = Math.min(scrollState.timelineEnd, centerDay + Math.floor(visibleDays) + BUFFER_DAYS);
    return { startDay, endDay };
  };

  // Get date for a day index - normalized to midnight to avoid timezone issues
  const getDateForDayIndex = (dayIndex) => {
    return addDaysNormalized(baseDate, dayIndex);
  };

  // Format day number
  const formatDay = (date) => {
    return date.getDate();
  };

  // Calculate visible day indices and related data
  const { visibleDayIndices, monthGroups, weekGroups, contentOffset, totalWidth } = useMemo(() => {
    const { startDay, endDay } = getVisibleDayRange();
    const indices = [];
    for (let i = startDay; i <= endDay; i++) {
      indices.push(i);
    }

    // Group consecutive visible days by month for header display
    const groups = [];
    let currentMonth = null;
    let currentGroup = null;

    indices.forEach((dayIndex, index) => {
      const day = getDateForDayIndex(dayIndex);
      const monthYear = `${formatMonth(day)} ${day.getFullYear()}`;

      if (monthYear !== currentMonth) {
        if (currentGroup) {
          groups.push(currentGroup);
        }
        currentGroup = {
          month: monthYear,
          startIndex: index,
          days: 1,
          actualStartDay: dayIndex
        };
        currentMonth = monthYear;
      } else if (currentGroup) {
        currentGroup.days++;
      }
    });

    if (currentGroup) {
      groups.push(currentGroup);
    }

    // Group consecutive visible days by ISO week number for header display
    const wGroups = [];
    let currentWeekKey = null;
    let currentWeekGroup = null;

    indices.forEach((dayIndex, index) => {
      const day = getDateForDayIndex(dayIndex);
      const weekKey = `${getISOWeekYear(day)}-${getISOWeek(day)}`;

      if (weekKey !== currentWeekKey) {
        if (currentWeekGroup) wGroups.push(currentWeekGroup);
        currentWeekGroup = {
          week: getISOWeek(day),
          startIndex: index,
          days: 1,
          actualStartDay: dayIndex,
        };
        currentWeekKey = weekKey;
      } else if (currentWeekGroup) {
        currentWeekGroup.days++;
      }
    });

    if (currentWeekGroup) wGroups.push(currentWeekGroup);

    const offset = (startDay - scrollState.timelineStart) * dayWidth;
    const totalDays = scrollState.timelineEnd - scrollState.timelineStart;
    const width = totalDays * dayWidth;

    return {
      visibleDayIndices: indices,
      monthGroups: groups,
      weekGroups: wGroups,
      contentOffset: offset,
      totalWidth: width
    };
  }, [scrollState, baseDate, dayWidth, visibleDays]);

  return {
    visibleDayIndices,
    monthGroups,
    weekGroups,
    contentOffset,
    totalWidth,
    getVisibleDayRange,
    getDateForDayIndex,
    formatDay
  };
};